import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { rgba } from 'polished';
import { usePopper } from 'react-popper';
import styled from 'styled-components';
import { useIsMounted } from '~/hooks';
import { colors } from '~/styles';
import DayPicker from './DayPicker';
import { useScrollVisibility } from './popperUtils';

const DropdownContainer = styled.div`
  position: relative;
`;

const DropdownPanel = styled.div`
  background-color: ${colors.white};
  border-radius: 0.3125rem;
  box-shadow: 0 0.1875rem 1rem ${rgba(colors.black, 0.25)};
  z-index: 1000;
`;

function getOffsetValue(offset) {
  if (typeof offset === 'number' && Number.isFinite(offset)) {
    return offset;
  }

  if (typeof offset !== 'string') {
    return 0;
  }

  const trimmedOffset = offset.trim();
  if (!trimmedOffset) {
    return 0;
  }

  if (trimmedOffset.endsWith('rem')) {
    const rem = Number.parseFloat(trimmedOffset);
    if (!Number.isFinite(rem)) {
      return 0;
    }

    const rootFontSize =
      typeof window === 'undefined' || typeof document === 'undefined'
        ? 16
        : Number.parseFloat(window.getComputedStyle(document.documentElement).fontSize) || 16;
    return rem * rootFontSize;
  }

  const value = Number.parseFloat(trimmedOffset);
  return Number.isFinite(value) ? value : 0;
}

function FixedDayPickerDropdown({
  isOpen: isOpenExternal,
  onClose,
  onChange,
  align = 'left',
  alignOffset = 0,
  position = 'bottom',
  positionOffset = 0,
  container: Container = DropdownContainer,
  panel: Panel = DropdownPanel,
  children,
  ...props
}) {
  const isControlled = typeof isOpenExternal === 'boolean';
  const [isOpenInternal, setIsOpenInternal] = useState(false);
  const isMounted = useIsMounted();
  const containerElement = useRef();
  const [referenceElement, setReferenceElement] = useState(null);
  const [popperElement, setPopperElement] = useState(null);

  const isOpen = useMemo(
    () => (isControlled ? isOpenExternal : isOpenInternal),
    [isControlled, isOpenExternal, isOpenInternal],
  );

  const setIsOpen = useCallback(
    (...args) => {
      if (!isMounted.current) return;

      if (isControlled) {
        const isOpen = args.length > 0 ? args[0] : null;
        if (isOpen === false && typeof onClose === 'function') {
          onClose();
        }
      } else {
        setIsOpenInternal(...args);
      }
    },
    [isControlled, isMounted, onClose],
  );

  const setContainerRef = useCallback(
    (node) => {
      containerElement.current = node;
      setReferenceElement(node);
    },
    [setReferenceElement],
  );

  const setPopperRef = useCallback(
    (node) => {
      setPopperElement(node);
    },
    [setPopperElement],
  );

  useEffect(() => {
    function handleClose(event) {
      const clickedInContainer = containerElement.current?.contains(event.target);
      const clickedInPopper = popperElement?.contains(event.target);

      if (!clickedInContainer && !clickedInPopper) {
        setIsOpen(false);
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClose);
    }

    return function cleanup() {
      document.removeEventListener('mousedown', handleClose);
    };
  }, [isOpen, popperElement, setIsOpen]);

  const placement = useMemo(() => {
    const vertical = position === 'top' ? 'top' : 'bottom';
    const horizontal = align === 'right' ? 'end' : 'start';
    return `${vertical}-${horizontal}`;
  }, [align, position]);

  const fallbackPlacements = useMemo(() => {
    const vertical = position === 'top' ? 'bottom' : 'top';
    const horizontal = align === 'right' ? 'end' : 'start';
    const oppositeHorizontal = horizontal === 'start' ? 'end' : 'start';

    return [`${vertical}-${horizontal}`, `${vertical}-${oppositeHorizontal}`];
  }, [align, position]);

  const skidding = useMemo(() => {
    const value = getOffsetValue(alignOffset);
    return align === 'right' ? -value : value;
  }, [align, alignOffset]);

  const distance = useMemo(() => getOffsetValue(positionOffset), [positionOffset]);

  const { styles, attributes } = usePopper(referenceElement, popperElement, {
    placement,
    strategy: 'fixed',
    modifiers: [
      { name: 'offset', options: { offset: [skidding, distance] } },
      { name: 'flip', options: { fallbackPlacements } },
    ],
  });

  const isVisible = useScrollVisibility(referenceElement, isOpen);

  const handleChange = (...args) => {
    if (!isControlled) {
      setIsOpen(false);
    }

    if (typeof onChange === 'function') {
      onChange(...args);
    }
  };

  return (
    <Container ref={setContainerRef}>
      {typeof children === 'function' ? children({ setIsOpen, isOpen }) : children}
      {isOpen &&
        isVisible &&
        ReactDOM.createPortal(
          <Panel
            ref={setPopperRef}
            align={align}
            alignOffset={alignOffset}
            position={position}
            positionOffset={positionOffset}
            data-day-picker-panel={true}
            style={styles.popper}
            {...attributes.popper}>
            <DayPicker {...props} onChange={handleChange} />
          </Panel>,
          document.body,
        )}
    </Container>
  );
}

export { DropdownContainer, DropdownPanel };
export default FixedDayPickerDropdown;
